import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { apiGet, apiPost, apiPut } from "../lib/api";
import { applyRegistry, type PdrFieldMeta } from "../lib/pdrFields";

export type RegistryField = PdrFieldMeta & {
  minimum?: number | null;
  maximum?: number | null;
  derived_from?: string;
};

export type PdrRegistry = {
  version: number;
  fields: RegistryField[];
  profiles: Record<string, string[]>;
};

export type RegistryVersion = {
  id: string;
  saved_at: string;
  note: string;
  field_count: number;
};

type RegistryResponse = {
  ok: boolean;
  registry: PdrRegistry;
  field_types: string[];
  versions: RegistryVersion[];
  uses_workspace_copy: boolean;
};

type PdrSchemaContextValue = {
  registry: PdrRegistry | null;
  fieldTypes: string[];
  versions: RegistryVersion[];
  loading: boolean;
  error: string;
  reload: () => Promise<void>;
  save: (registry: { fields: RegistryField[]; profiles: Record<string, string[]> }, note?: string) => Promise<void>;
  rollback: (versionId: string) => Promise<void>;
};

const PdrSchemaContext = createContext<PdrSchemaContextValue | null>(null);

function ingest(res: RegistryResponse): {
  registry: PdrRegistry;
  fieldTypes: string[];
  versions: RegistryVersion[];
} {
  applyRegistry(res.registry);
  return {
    registry: res.registry,
    fieldTypes: res.field_types ?? [],
    versions: res.versions ?? [],
  };
}

export function PdrSchemaProvider({ children }: { children: ReactNode }) {
  const [registry, setRegistry] = useState<PdrRegistry | null>(null);
  const [fieldTypes, setFieldTypes] = useState<string[]>([]);
  const [versions, setVersions] = useState<RegistryVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    const res = await apiGet<RegistryResponse>("/api/pdr/fields", { timeoutMs: 20_000 });
    const next = ingest(res);
    setRegistry(next.registry);
    setFieldTypes(next.fieldTypes);
    setVersions(next.versions);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await reload();
        if (!cancelled) setError("");
      } catch (e) {
        // Keep the bundled seed defaults; the desk still works read-only.
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load PDR schema");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const save = useCallback(
    async (
      reg: { fields: RegistryField[]; profiles: Record<string, string[]> },
      note = "ui-save",
    ) => {
      const res = await apiPut<RegistryResponse>("/api/pdr/fields", {
        fields: reg.fields,
        profiles: reg.profiles,
        note,
      });
      const next = ingest(res);
      setRegistry(next.registry);
      setFieldTypes(next.fieldTypes);
      setVersions(next.versions);
    },
    [],
  );

  const rollback = useCallback(async (versionId: string) => {
    const res = await apiPost<RegistryResponse>("/api/pdr/fields/rollback", {
      version_id: versionId,
    });
    const next = ingest(res);
    setRegistry(next.registry);
    setFieldTypes(next.fieldTypes);
    setVersions(next.versions);
  }, []);

  const value = useMemo(
    () => ({ registry, fieldTypes, versions, loading, error, reload, save, rollback }),
    [registry, fieldTypes, versions, loading, error, reload, save, rollback],
  );

  return <PdrSchemaContext.Provider value={value}>{children}</PdrSchemaContext.Provider>;
}

export function usePdrSchema(): PdrSchemaContextValue {
  const ctx = useContext(PdrSchemaContext);
  if (!ctx) throw new Error("usePdrSchema must be used within PdrSchemaProvider");
  return ctx;
}
